import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Package, Globe, ChevronRight, Search, Plane } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DestinationsIndexPage() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPackages, setTotalPackages] = useState(0);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/seo/destinations`);
        
        if (!response.ok) {
          throw new Error('Nu s-au putut încărca destinațiile');
        }
        
        const data = await response.json();
        setDestinations(data);
        setTotalPackages(data.reduce((sum, d) => sum + d.count, 0));
      } catch (err) {
        console.error('Error loading destinations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, []);

  const filteredDestinations = destinations.filter(d => 
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by first letter for better organization
  const groupedDestinations = filteredDestinations.reduce((acc, dest) => {
    const letter = dest.name[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(dest);
    return acc;
  }, {});

  const canonicalUrl = 'https://vaai.ro/destinatii';
  const descriptionText = 'Explorează ' + destinations.length + '+ destinații turistice populare. ' + totalPackages + ' pachete de vacanță disponibile: circuite, sejururi, city break-uri. Rezervă online la cele mai bune prețuri!';
  const ogDescriptionText = 'Explorează ' + destinations.length + '+ destinații turistice cu ' + totalPackages + ' pachete disponibile.';

  return (
    <div className="min-h-screen bg-navy pt-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Destinații Turistice 2026 - Pachete Vacanță | VaAi</title>
        <meta name="description" content={descriptionText} />
        <meta name="keywords" content="destinatii turistice, vacante 2026, circuite externe, sejururi, city break, pachete turistice, vacanta grecia, vacanta turcia, circuit italia" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Destinații Turistice 2026 | VaAi " />
        <meta property="og:description" content={ogDescriptionText} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="VaAi" />
        <meta property="og:locale" content="ro_RO" />
        
        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <Link to="/" className="text-white/60 hover:text-gold transition-colors">
              Acasă
            </Link>
          </li>
          <ChevronRight className="w-4 h-4 text-white/40" />
          <li>
            <span className="text-gold font-medium">Destinații</span>
          </li>
        </ol>
      </nav>

      {/* Hero Header */}
      <header className="bg-gradient-to-b from-navy-light to-navy py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center flex-shrink-0">
              <Globe className="w-8 h-8 text-gold" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                Destinații Turistice 2026
              </h1>
              <p className="text-white/70 mt-2 max-w-2xl">
                Explorează {destinations.length} destinații populare cu {totalPackages} pachete de vacanță disponibile. 
                Circuite, sejururi și city break-uri la cele mai bune prețuri.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
              <MapPin className="w-5 h-5 text-gold" />
              <div>
                <p className="text-2xl font-bold text-white">{destinations.length}</p>
                <p className="text-xs text-white/60">Destinații</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
              <Package className="w-5 h-5 text-gold" />
              <div>
                <p className="text-2xl font-bold text-white">{totalPackages}</p>
                <p className="text-xs text-white/60">Pachete</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
              <Plane className="w-5 h-5 text-gold" />
              <div>
                <p className="text-sm font-medium text-white">Avion & Autocar</p>
                <p className="text-xs text-white/60">Transport</p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Caută o destinație..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>
        </div>
      </header>

      {/* Destinations Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Popular destinations first */}
            {!searchTerm && (
              <div className="mb-12">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <span className="text-gold">★</span> Destinații Populare
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {destinations.slice(0, 8).map((dest) => (
                    <DestinationCard key={dest.slug} destination={dest} featured />
                  ))}
                </div>
              </div>
            )}

            {/* All destinations alphabetically */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-6">
                {searchTerm ? `Rezultate pentru "${searchTerm}"` : 'Toate Destinațiile'}
              </h2>
              
              {Object.keys(groupedDestinations).length === 0 ? (
                <p className="text-white/60 text-center py-8">
                  Nu am găsit destinații pentru "{searchTerm}"
                </p>
              ) : (
                Object.keys(groupedDestinations).sort().map(letter => (
                  <div key={letter} className="mb-8">
                    <h3 className="text-gold font-bold text-lg mb-4 border-b border-white/10 pb-2">
                      {letter}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {groupedDestinations[letter].map((dest) => (
                        <DestinationCard key={dest.slug} destination={dest} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </section>

      {/* SEO Content */}
      <section className="bg-navy-light border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-xl font-semibold text-white mb-6">
            Găsește vacanța perfectă pentru tine
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-white/70 text-sm">
            <div>
              <h3 className="text-white font-medium mb-2">Circuite Externe</h3>
              <p>
                Descoperă Europa și lumea prin circuite organizate cu ghid. 
                Vizitează mai multe orașe și atracții într-o singură călătorie.
              </p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-2">Sejururi la Mare</h3>
              <p>
                Relaxează-te pe cele mai frumoase plaje din Grecia, Turcia, Egipt sau Bulgaria. 
                Pachete all-inclusive la hoteluri de top.
              </p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-2">City Break</h3>
              <p>
                Evadări de weekend în cele mai frumoase orașe europene. 
                Roma, Paris, Barcelona, Viena și multe altele.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Destination Card Component
function DestinationCard({ destination, featured = false }) {
  return (
    <Link
      to={`/destinatii/${destination.slug}`}
      className={`group block bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-[1.02] ${featured ? 'ring-1 ring-gold/20' : ''}`}
    >
      {/* Image */}
      {destination.image_url && (
        <div className="aspect-video overflow-hidden">
          <img
            src={destination.image_url}
            alt={`Vacanță în ${destination.name}`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gold" />
            <h3 className="font-semibold text-white group-hover:text-gold transition-colors">
              {destination.name}
            </h3>
          </div>
          <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-gold group-hover:translate-x-1 transition-all" />
        </div>
        <p className="text-sm text-white/60 mt-1">
          {destination.count} {destination.count === 1 ? 'pachet' : 'pachete'} disponibile
        </p>
      </div>
    </Link>
  );
}
